import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { UPDATE_BUSINESS_MUTATION } from '@graphql/mutations/organization/organizationMutations';
import type { UpdateBusinessMutation, UpdateBusinessMutationVariables } from '@graphql/types';
import { useCallback } from 'react';

import type { UpdateOrganizationInput } from '../types';

export const useUpdateBusiness = () => {
  const [updateBusinessMutation, { loading }] = useMutation<
    UpdateBusinessMutation,
    UpdateBusinessMutationVariables
  >(UPDATE_BUSINESS_MUTATION);

  const doUpdateBusiness = useCallback(
    async (input: UpdateOrganizationInput) => {
      // TODO: When BE adds id as argument, uncomment and add id to variables:
      // variables: {
      //   id: orgId,
      //   input,
      // }
      const response = await updateBusinessMutation({
        variables: {
          input,
        },
      });

      const businessRes = response.data?.updateBusiness;

      if (!businessRes) {
        notify.error({ title: 'Business update failed', description: 'No response from server' });
        throw new Error('Business update failed');
      }

      // Check for errors in the response - show error toast if there are errors
      if (businessRes.errors && businessRes.errors.length > 0) {
        const errorMessage =
          businessRes.errors[0]?.message || 'Failed to update business. Please try again.';

        notify.error({ title: 'Business update failed', description: errorMessage });

        return businessRes;
      }

      // Show success toast if the business was updated successfully
      if (businessRes.organization) {
        notify.success({ title: 'Business updated successfully' });
      }

      return businessRes;
    },
    [updateBusinessMutation]
  );

  return { doUpdateBusiness, updateBusinessLoading: loading };
};
